<template>
    <HomeFrame class="home" header-value="0" hero-image="1-1.png" :hero-gap="0">
        <div class="main">
            <section class="intro-wrap" :style="introWrapStyle">
                <div class="section-shell">
                    <section class="action-section">
                        <div
                            class="action-card"
                            v-for="card in actionCards"
                            :key="card.title"
                            @click="handleAction(card)"
                        >
                            <div class="action-title">{{ card.title }}</div>
                            <div class="action-button">{{ card.button }}</div>
                        </div>
                    </section>

                    <section class="content-section intro-section">
                        <div class="section-title">
                            {{ $t("home.expoProfileTitle") }}
                        </div>
                        <div class="intro-panel">
                            <div class="intro-copy" :class="{ en: isEnglish }">
                                {{ $t("home.expoProfileText") }}
                            </div>
                            <div class="intro-media">
                                <video
                                    class="intro-video"
                                    :src="introVideo"
                                    controls
                                    preload="metadata"
                                ></video>
                            </div>
                        </div>
                    </section>
                </div>
            </section>

            <section class="data-section" :style="dataWrapStyle">
                <div class="section-shell">
                    <div class="section-title section-title-light">
                        {{ dataTitle }}
                    </div>
                    <div class="stats-grid" :class="{ en: isEnglish }">
                        <div
                            class="stat-card"
                            v-for="item in statsList"
                            :key="item.label"
                        >
                            <div class="stat-value">{{ item.value }}</div>
                            <div class="stat-meta">
                                <div class="stat-label">{{ item.label }}</div>
                                <div class="stat-unit">{{ item.unit }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="content-section news-section">
                <div class="section-shell">
                    <div class="section-title">
                        {{ workTitle }}
                    </div>
                    <div class="news-panel">
                        <div class="news-list" :class="{ en: isEnglish }">
                            <div
                                class="news-item"
                                v-for="item in featuredNews"
                                :key="item.id"
                                @click="openWorkNews(item)"
                            >
                                <div class="news-date">{{ formatDate(item.date) }}</div>
                                <div class="news-text">{{ formatWorkNewsTitle(item) }}</div>
                            </div>
                        </div>
                        <div class="news-cover">
                            <swiper
                                v-if="newsCarouselItems.length > 1"
                                class="news-carousel"
                                :options="newsSwiperOption"
                            >
                                <swiper-slide
                                    v-for="item in newsCarouselItems"
                                    :key="item.id"
                                >
                                    <div
                                        class="news-carousel-slide"
                                        @click="openWorkNews(item)"
                                    >
                                        <img :src="item.cover" :alt="item.title" />
                                        <div class="news-carousel-caption">
                                            {{ formatWorkNewsTitle(item) }}
                                        </div>
                                    </div>
                                </swiper-slide>
                                <div
                                    slot="pagination"
                                    class="swiper-pagination news-carousel-pagination"
                                ></div>
                            </swiper>
                            <div
                                v-else-if="newsCarouselItems[0]"
                                class="news-carousel-slide news-carousel-slide-single"
                                @click="openWorkNews(newsCarouselItems[0])"
                            >
                                <img
                                    :src="newsCarouselItems[0].cover"
                                    :alt="newsCarouselItems[0].title"
                                />
                                <div class="news-carousel-caption">
                                    {{ formatWorkNewsTitle(newsCarouselItems[0]) }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="news-actions">
                        <button
                            type="button"
                            class="news-more-button"
                            @click="openWorkNewsList"
                        >
                            {{ newsMoreText }}
                        </button>
                    </div>
                </div>
            </section>

            <section class="gallery-section">
                <div class="section-shell">
                    <div class="section-title section-title-light">
                        {{ galleryTitle }}
                    </div>
                    <div class="gallery-marquee">
                        <div
                            class="gallery-item"
                            v-for="(photo, index) in duplicatedGalleryPhotos"
                            :key="photo.img + '-' + index"
                        >
                            <img :src="photo.img" :alt="photo.alt" />
                        </div>
                    </div>
                </div>
            </section>

            <section class="directory-section">
                <div class="section-shell">
                    <div class="section-title">{{ directoryTitle }}</div>
                    <div class="section-note">{{ directoryNote }}</div>
                    <div class="logo-grid">
                        <div
                            class="logo-card"
                            v-for="item in visibleLogoList"
                            :key="item.img || item.name"
                        >
                            <img :src="item.img" :alt="item.name || 'logo'" />
                        </div>
                    </div>
                    <div
                        v-if="shopLogoList.length > logoPreviewCount"
                        class="directory-more"
                        @click="openDirectoryMore"
                    >
                        <div class="directory-more-text">{{ moreText }}</div>
                        <div class="directory-more-icon"></div>
                    </div>
                </div>
            </section>
        </div>
        <div
            v-if="showTalentFloat"
            class="talent-float"
            ref="talentFloat"
            :style="talentFloatStyle"
            @mouseenter="pauseTalentFloat"
            @mouseleave="resumeTalentFloat"
        >
            <button
                type="button"
                class="talent-float__close"
                @click.stop="closeTalentFloat"
            >
                ×
            </button>
            <img
                class="talent-float__image"
                :src="talentFloatImage"
                alt="人才对接活动"
                @click="openTalentRecruitment"
            />
        </div>
    </HomeFrame>
</template>

<script>
import HomeFrame from "@/components/HomeFrame.vue";
import { ABOUT_SECTIONS, getAboutWorkNews } from "@/data/aboutShow";
import { firmsList } from "@/utils/map";
import { gm } from "@/utils/gm";

const EN_EXPO_TITLE_PREFIX =
    "2026 Jiangsu Green & Low-Carbon Building International Expo | ";
const TALENT_FLOAT_SESSION_KEY = "homeTalentFloatDismissed";

export default {
    components: {
        HomeFrame,
    },
    data() {
        return {
            shopLogoList: [],
            logoPreviewCount: 40,
            introVideo: require("@/assets/file/video.mp4"),
            talentFloatImage: "/global/images/talent-float.jpg",
            showTalentFloat: false,
            isTalentFloatPaused: false,
            isTalentFloatHovered: false,
            talentFloatReady: false,
            talentFloatRect: {
                width: 0,
                height: 0,
            },
            talentFloatPos: {
                x: 0,
                y: 0,
            },
            talentFloatVelocity: {
                x: 168, // px/s
                y: 136, // px/s
            },
            talentFloatBounds: {
                minX: 12,
                maxX: 12,
                minY: 80,
                maxY: 80,
            },
            talentFloatRafId: null,
            talentFloatLastTick: 0,
            talentFloatSafeGap: 12,
            talentFloatHeaderGap: 80,
        };
    },
    created() {
        this.shopLogoList = firmsList.filter((item) => item.img);
        const dismissed = sessionStorage.getItem(TALENT_FLOAT_SESSION_KEY);
        this.showTalentFloat = dismissed !== "1" && this.shouldShowTalentFloat();
    },
    mounted() {
        this.toggleHomeResponsiveLayout(true);
        if (this.showTalentFloat) {
            this.initTalentFloat();
        }
        window.addEventListener("resize", this.handleTalentFloatResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleTalentFloatResize);
        this.stopTalentFloatAnimation();
        this.toggleHomeResponsiveLayout(false);
    },
    computed: {
        isEnglish() {
            return this.$i18n.locale === "en";
        },
        actionCards() {
            return [
                {
                    title: this.isEnglish ? "Exhibitor Registration" : "展商报名",
                    button: this.isEnglish ? "Click to register" : "点击报名",
                    type: "register",
                    value: 1,
                },
                {
                    title: this.isEnglish ? "Visitor Registration" : "观众登记",
                    button: this.isEnglish ? "Click to register" : "点击登记",
                    type: "register",
                    value: 2,
                },
                {
                    title: this.isEnglish ? "Exhibition Highlights" : "本届亮点",
                    button: this.isEnglish ? "View more" : "查看更多",
                    type: "route",
                    value: "/highlights",
                },
                {
                    title: this.isEnglish ? "Download Center" : "下载中心",
                    button: this.isEnglish ? "Click to open" : "点击进入",
                    type: "route",
                    value: "/downloads",
                },
                {
                    title: this.isEnglish ? "Live Room" : "直播间",
                    button: this.isEnglish ? "Click to watch" : "点击观看",
                    type: "live",
                },
            ];
        },
        statsList() {
            return this.isEnglish
                ? [
                      { value: "2", label: "Halls", unit: "" },
                      { value: "1200", label: "Standard Booths", unit: "" },
                      { value: "24000", label: "Planned Scale", unit: "㎡" },
                      { value: "40000", label: "Expected Visitors", unit: "" },
                  ]
                : [
                      { value: "2", label: "展馆", unit: "个" },
                      { value: "1200", label: "标准展位", unit: "个" },
                      { value: "2.4", label: "预计展出规模", unit: "万平方米" },
                      { value: "4", label: "预计观众", unit: "万人" },
                  ];
        },
        dataTitle() {
            return this.isEnglish ? "EXPO DATA" : "本届博览会大数据";
        },
        workTitle() {
            return this.isEnglish ? "WORK UPDATES" : "工作动态";
        },
        galleryTitle() {
            return this.isEnglish ? "PREVIOUS PHOTOS" : "往届照片";
        },
        directoryTitle() {
            return this.isEnglish ? "2026 EXHIBITOR DIRECTORY" : "2026展商名录";
        },
        directoryNote() {
            return this.isEnglish
                ? "Exhibitors are displayed in random order"
                : "展商顺序随机排列";
        },
        moreText() {
            return this.isEnglish ? "View More" : "查看更多";
        },
        newsMoreText() {
            return this.isEnglish ? "Show More" : "显示更多";
        },
        workNewsList() {
            return getAboutWorkNews(this.$i18n.locale);
        },
        featuredNews() {
            return this.workNewsList.slice(0, 5);
        },
        newsCarouselItems() {
            return this.workNewsList.filter((item) => item.cover);
        },
        newsSwiperOption() {
            return {
                loop: this.newsCarouselItems.length > 1,
                observer: true,
                observeParents: true,
                watchOverflow: true,
                speed: 600,
                autoplay:
                    this.newsCarouselItems.length > 1
                        ? {
                              delay: 4500,
                              disableOnInteraction: false,
                          }
                        : false,
                pagination: {
                    el: ".news-carousel-pagination",
                    clickable: true,
                },
                a11y: false,
            };
        },
        galleryPhotos() {
            return [
                {
                    img: gm("微信图片_20260307141220_432_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 1" : "往届照片1",
                },
                {
                    img: gm("微信图片_20260307141221_433_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 2" : "往届照片2",
                },
                {
                    img: gm("微信图片_20260307141222_434_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 3" : "往届照片3",
                },
                {
                    img: gm("微信图片_20260307141224_435_14.png"),
                    alt: this.isEnglish ? "Expo photo 4" : "往届照片4",
                },
                {
                    img: gm("微信图片_20260307141225_436_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 5" : "往届照片5",
                },
                {
                    img: gm("微信图片_20260307141226_437_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 6" : "往届照片6",
                },
                {
                    img: gm("微信图片_20260307141227_438_14.png"),
                    alt: this.isEnglish ? "Expo photo 7" : "往届照片7",
                },
                {
                    img: gm("微信图片_20260307141228_439_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 8" : "往届照片8",
                },
                {
                    img: gm("微信图片_20260307143109_441_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 9" : "往届照片9",
                },
                {
                    img: gm("微信图片_20260307143110_442_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 10" : "往届照片10",
                },
                {
                    img: gm("微信图片_20260307143112_443_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 11" : "往届照片11",
                },
                {
                    img: gm("微信图片_20260307143114_444_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 12" : "往届照片12",
                },
                {
                    img: gm("微信图片_20260307143115_445_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 13" : "往届照片13",
                },
                {
                    img: gm("微信图片_20260307143116_446_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 14" : "往届照片14",
                },
                {
                    img: gm("微信图片_20260307143118_447_14.jpg"),
                    alt: this.isEnglish ? "Expo photo 15" : "往届照片15",
                },
            ];
        },
        duplicatedGalleryPhotos() {
            return [...this.galleryPhotos, ...this.galleryPhotos];
        },
        visibleLogoList() {
            return this.shopLogoList.slice(0, this.logoPreviewCount);
        },
        introWrapStyle() {
            return {
                backgroundImage: `url(${require("@/assets/image/home-bg-1.png")})`,
            };
        },
        dataWrapStyle() {
            return {
                backgroundImage: `url(${require("@/assets/image/home-bg-2.png")})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                backgroundSize: "cover",
            };
        },
        talentFloatStyle() {
            if (!this.talentFloatReady) {
                return {
                    visibility: "hidden",
                };
            }
            return {
                transform: `translate3d(${Math.round(this.talentFloatPos.x)}px, ${Math.round(this.talentFloatPos.y - (this.isTalentFloatHovered ? 3 : 0))}px, 0)`,
            };
        },
    },
    methods: {
        shouldShowTalentFloat() {
            const ua = navigator.userAgent || "";
            const isMobileUA = /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i.test(
                ua
            );
            return !isMobileUA;
        },
        formatWorkNewsTitle(item) {
            if (!item) {
                return "";
            }
            const normalizedTitle = this.isEnglish
                ? String(item.title).replace(/\|/g, " | ")
                : item.title;
            const firstFeaturedId =
                this.featuredNews && this.featuredNews[0]
                    ? this.featuredNews[0].id
                    : null;
            const shouldSkipPrefixForFirstItem =
                this.isEnglish && firstFeaturedId && item.id === firstFeaturedId;
            if (
                this.isEnglish &&
                item.hasEnglishContent &&
                !shouldSkipPrefixForFirstItem &&
                !String(item.title).startsWith(EN_EXPO_TITLE_PREFIX)
            ) {
                return `${EN_EXPO_TITLE_PREFIX}${normalizedTitle}`;
            }
            return normalizedTitle;
        },
        toggleHomeResponsiveLayout(enabled) {
            const appRoot = document.getElementById("app");

            if (appRoot) {
                appRoot.classList[enabled ? "add" : "remove"](
                    "home-responsive-layout"
                );
            }
        },
        register(type) {
            if (type === 1) {
                this.goRoute("/contactUs");
                return;
            }
            if (type === 2) {
                window.open("https://z.zcyit.cn/green/#/?regid=1191", "_blank");
                return;
            }
            this.$router.push("/register?type=" + type);
        },
        goRoute(path) {
            if (path !== this.$route.path) {
                this.$router.push(path);
                window.scrollTo(0, 0);
            }
        },
        openLink(link) {
            if (link) {
                window.open(link, "_blank");
            }
        },
        openWorkNews(item) {
            if (!item) {
                return;
            }

            this.$router.push({
                path: "/about",
                query: {
                    section: ABOUT_SECTIONS.news,
                    article: item.id,
                },
            });
            window.scrollTo(0, 0);
        },
        showComingSoonDialog() {
            this.$alert(
                this.$t("exhibitor.comingSoon"),
                this.$t("exhibitor.tipTitle"),
                {
                    confirmButtonText: this.$t("exhibitor.confirm"),
                    showClose: false,
                }
            );
        },
        formatDate(value) {
            return value ? value.replace(/-/g, "/") : "";
        },
        handleAction(card) {
            if (card.type === "register") {
                this.register(card.value);
                return;
            }
            if (card.type === "route") {
                this.goRoute(card.value);
                return;
            }
            if (card.type === "live") {
                this.showComingSoonDialog();
            }
        },
        openGalleryMore() {
            this.goRoute("/about");
        },
        openWorkNewsList() {
            this.$router.push({
                path: "/about",
                query: {
                    section: ABOUT_SECTIONS.news,
                },
            });
            window.scrollTo(0, 0);
        },
        openDirectoryMore() {
            this.$router.push({
                path: "/exhibitor",
                query: {
                    index: "1",
                },
            });
            window.scrollTo(0, 0);
        },
        openTalentRecruitment() {
            this.$router.push({
                path: "/maneuver",
                query: {
                    index: "2",
                },
            });
            window.scrollTo(0, 0);
        },
        initTalentFloat() {
            this.$nextTick(() => {
                if (!this.showTalentFloat) {
                    return;
                }
                this.syncTalentFloatRectAndBounds(true);
                this.talentFloatReady = true;
                this.startTalentFloatAnimation();
            });
        },
        syncTalentFloatRectAndBounds(resetPosition) {
            const floatEl = this.$refs.talentFloat;
            if (!floatEl) {
                return;
            }
            const rect = floatEl.getBoundingClientRect();
            const width = Math.max(rect.width || 300, 220);
            const height = Math.max(rect.height || 80, 56);
            const maxX = Math.max(
                this.talentFloatSafeGap,
                window.innerWidth - width - this.talentFloatSafeGap
            );
            const maxY = Math.max(
                this.talentFloatHeaderGap,
                window.innerHeight - height - this.talentFloatSafeGap
            );
            this.talentFloatRect.width = width;
            this.talentFloatRect.height = height;
            this.talentFloatBounds = {
                minX: this.talentFloatSafeGap,
                maxX,
                minY: this.talentFloatHeaderGap,
                maxY,
            };
            if (resetPosition) {
                this.talentFloatPos = {
                    x: maxX,
                    y: maxY,
                };
                return;
            }
            this.talentFloatPos.x = Math.min(
                this.talentFloatBounds.maxX,
                Math.max(this.talentFloatBounds.minX, this.talentFloatPos.x)
            );
            this.talentFloatPos.y = Math.min(
                this.talentFloatBounds.maxY,
                Math.max(this.talentFloatBounds.minY, this.talentFloatPos.y)
            );
        },
        handleTalentFloatResize() {
            if (!this.showTalentFloat) {
                return;
            }
            this.syncTalentFloatRectAndBounds(false);
        },
        startTalentFloatAnimation() {
            if (this.talentFloatRafId) {
                return;
            }
            this.talentFloatLastTick = 0;
            this.talentFloatRafId = window.requestAnimationFrame(
                this.updateTalentFloatPosition
            );
        },
        stopTalentFloatAnimation() {
            if (!this.talentFloatRafId) {
                return;
            }
            window.cancelAnimationFrame(this.talentFloatRafId);
            this.talentFloatRafId = null;
            this.talentFloatLastTick = 0;
        },
        updateTalentFloatPosition(timestamp) {
            if (!this.showTalentFloat) {
                this.stopTalentFloatAnimation();
                return;
            }
            if (!this.talentFloatLastTick) {
                this.talentFloatLastTick = timestamp;
            }
            const deltaSec = Math.min(
                0.033,
                Math.max(0, (timestamp - this.talentFloatLastTick) / 1000)
            );
            this.talentFloatLastTick = timestamp;
            if (!this.isTalentFloatPaused && deltaSec > 0) {
                let nextX =
                    this.talentFloatPos.x + this.talentFloatVelocity.x * deltaSec;
                let nextY =
                    this.talentFloatPos.y + this.talentFloatVelocity.y * deltaSec;

                if (nextX <= this.talentFloatBounds.minX) {
                    nextX = this.talentFloatBounds.minX;
                    this.talentFloatVelocity.x = Math.abs(this.talentFloatVelocity.x);
                } else if (nextX >= this.talentFloatBounds.maxX) {
                    nextX = this.talentFloatBounds.maxX;
                    this.talentFloatVelocity.x = -Math.abs(this.talentFloatVelocity.x);
                }

                if (nextY <= this.talentFloatBounds.minY) {
                    nextY = this.talentFloatBounds.minY;
                    this.talentFloatVelocity.y = Math.abs(this.talentFloatVelocity.y);
                } else if (nextY >= this.talentFloatBounds.maxY) {
                    nextY = this.talentFloatBounds.maxY;
                    this.talentFloatVelocity.y = -Math.abs(this.talentFloatVelocity.y);
                }

                this.talentFloatPos.x = nextX;
                this.talentFloatPos.y = nextY;
            }
            this.talentFloatRafId = window.requestAnimationFrame(
                this.updateTalentFloatPosition
            );
        },
        pauseTalentFloat() {
            this.isTalentFloatHovered = true;
            this.isTalentFloatPaused = true;
        },
        resumeTalentFloat() {
            this.isTalentFloatHovered = false;
            this.isTalentFloatPaused = false;
        },
        closeTalentFloat() {
            sessionStorage.setItem(TALENT_FLOAT_SESSION_KEY, "1");
            this.showTalentFloat = false;
            this.stopTalentFloatAnimation();
        },
    },
};
</script>

<style scoped lang="less">
.home {
    width: 100%;
    min-width: 0;
}

.main {
    background:
        linear-gradient(120deg, rgba(219, 225, 235, 0.32) 0, rgba(219, 225, 235, 0.32) 1px, transparent 1px, transparent 60px),
        linear-gradient(145deg, #eff2f6 0%, #ffffff 42%, #f3f5f8 100%);
    background-size: 92px 92px, auto;
}

.section-shell {
    width: 88%;
    margin: 0 auto;
}

.section-title {
    font-size: 36px;
    line-height: 1.2;
    font-weight: 700;
    color: #333333;
    text-align: center;
}

.section-title-light {
    color: #ffffff;
}

.intro-wrap {
    background-repeat: no-repeat;
    background-position: center top;
    background-size: cover;
    padding: 50px 0 54px;
}

.action-section {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: clamp(6px, 0.9vw, 16px);
}

.action-card {
    min-height: clamp(72px, 7vw, 110px);
    height: clamp(88px, 9vw, 140px); padding: clamp(10px, 1.2vw, 20px) clamp(4px, 0.4vw, 8px);box-sizing:border-box;
    border-radius: 10px;
    background: linear-gradient(180deg, #5a3198 0%, #2843a5 100%);
    color: #ffffff;
    text-align: center;
    cursor: pointer;
    box-shadow: 0 14px 28px rgba(44, 56, 139, 0.18);
    display:flex; flex-direction:row; flex-wrap:wrap; justify-content:center; overflow:hidden; clear:both;align-items:center;
}

.action-title {box-sizing:border-box;
    width: 100%;
    display: block;
    font-size: clamp(12px, 1.45vw, 30px);
    line-height: 1.15;
    font-weight: 500;
    margin: 0 ;padding: 0;
}

.action-button {
    width: fit-content;box-sizing:border-box;
    min-width: 0;
    max-width: 100%;
    margin: 0px auto 0;
    padding: clamp(4px, 0.35vw, 5px) clamp(6px, 0.9vw, 16px);
    border: 1px solid rgba(255, 255, 255, 0.82);
    font-size: clamp(9px, 0.82vw, 16px);
    line-height: 1;
    border-radius: 5px;
}

.content-section {
    padding: 72px 0 0;
}

.intro-panel,
.news-panel {
    margin-top: 34px;
    display: grid;
    grid-template-columns: minmax(0, 1.18fr) minmax(520px, 0.82fr);
    gap: 54px;
    align-items: center;
}

.intro-copy {
    min-height: 252px;
    padding: 42px 48px;
    font-size: 24px;
    line-height: 50px;
    color: #333333;
    text-align: justify;
    text-indent: 2em;
}

.intro-copy.en {
    text-indent: 0;
}

.section-title,
.action-title,
.stat-label,
.stat-unit,
.section-note,
.directory-more-text,
.action-button {
    overflow-wrap: anywhere;
}

.intro-media,
.news-cover,
.gallery-item {
    position: relative;
    overflow: hidden;
}

.intro-media,
.news-cover {
    cursor: pointer;
}

.intro-video,
.news-cover img,
.gallery-item img,
.logo-card img {
    width: 100%;
    display: block;
}

.intro-media {
    border: 1px solid rgba(29, 50, 116, 0.08);
    aspect-ratio: 16 / 9;
}

.intro-video {
    height: 100%;
    min-height: 252px;
    object-fit: cover;
}

.data-section,
.gallery-section {
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
}

.data-section {
    padding: 32px 0 36px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 30px;
    gap: 0;
}

.stat-card {
    min-height: 164px;
    padding: 28px 26px 24px;
    min-width: 0;
    gap: 14px;
    background: rgba(31, 53, 145, 0.2);
    display:flex; flex-direction:row; flex-wrap:no-wrap; justify-content:center; overflow:hidden; clear:both;align-items:center;
}

.stat-value {
    font-size: 80px;
    line-height: 1;
    font-weight: 700;
    font-style: italic;
    color: #2ce1ff;
}

.stat-meta {
    min-width: 0;
    color: #ffffff;
}

.stat-label {
    font-size: 26px;
    line-height: 1.35;
    font-weight: 600;
    white-space: nowrap;
}

.stat-unit {
    margin-top: 8px;
    font-size: 20px;
}

.stats-grid.en .stat-card {
    padding: 20px 12px 18px;
    gap: clamp(6px, 0.55vw, 10px);
    flex-direction: row;
    align-items: center;
    justify-content: center;
}

.stats-grid.en .stat-value {
    font-size: clamp(30px, 3.4vw, 60px);
    line-height: 1;
}

.stats-grid.en .stat-label {
    font-size: clamp(11px, 0.95vw, 16px);
    line-height: 1.2;
}

.stats-grid.en .stat-unit {
    margin-top: 4px;
    font-size: clamp(10px, 0.78vw, 13px);
    line-height: 1.2;
}

.news-section {
    padding: 58px 0 70px;
}

.news-list {
    display: flex;
    flex-direction: column;
    gap: 22px;
}

.news-item {
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    cursor: pointer;
    color: #333333;
}

.news-item:hover .news-date,
.news-item:hover .news-text {
    color: #184ef6;
}

.news-date {
    color: #333;
    font-size: 24px;
    line-height: 1.65;
}

.news-text {
    font-size: 24px;
    line-height: 1.65;
}

.talent-float {
    position: fixed;
    left: 0;
    top: 0;
    width: min(420px, 32vw);
    min-width: 300px;
    z-index: 2100;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(18, 54, 194, 0.14);
    box-shadow:
        0 10px 24px rgba(15, 23, 42, 0.14),
        0 2px 8px rgba(15, 23, 42, 0.08);
    background: #fff;
    transition:
        box-shadow 0.24s ease,
        transform 0.08s linear;
    will-change: transform;
}

.talent-float:hover {
    box-shadow:
        0 16px 28px rgba(15, 23, 42, 0.2),
        0 6px 12px rgba(15, 23, 42, 0.12);
}

.talent-float__image {
    width: 100%;
    display: block;
    cursor: pointer;
}

.talent-float__close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 50%;
    color: #ffffff;
    background: rgba(17, 24, 39, 0.5);
    font-size: 20px;
    line-height: 34px;
    text-align: center;
    cursor: pointer;
    padding: 0;
    transition: background-color 0.2s ease;
}

.talent-float__close:hover {
    background: rgba(17, 24, 39, 0.72);
}

.news-list.en .news-item {
    grid-template-columns: 200px minmax(0, 1fr);
    align-items: start;
}

.news-list.en .news-date {
    font-size: 21px;
}

.news-list.en .news-text {
    font-size: 21px;
    line-height: 1.45;
    overflow-wrap: anywhere;
    word-break: break-all;
    display: block;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    text-align: left;
    min-height: 0;
}

.news-cover {
    height: 380px;
    border-radius: 18px;
    background: #e9edf5;
    box-shadow: 0 20px 40px rgba(23, 47, 108, 0.12);
}

.news-actions {
    margin-top: 28px;
    display: flex;
    justify-content: center;
}

.news-more-button {
    min-width: 168px;
    padding: 14px 28px;
    border: 1px solid #184ef6;
    border-radius: 999px;
    background: #ffffff;
    color: #184ef6;
    font-size: 18px;
    line-height: 1.2;
    font-weight: 600;
    cursor: pointer;
    transition:
        background-color 0.2s ease,
        color 0.2s ease,
        box-shadow 0.2s ease,
        transform 0.2s ease;
}

.news-more-button:hover {
    background: #184ef6;
    color: #ffffff;
    box-shadow: 0 12px 24px rgba(24, 78, 246, 0.22);
    transform: translateY(-1px);
}

.news-carousel,
.news-carousel-slide,
.news-carousel-slide-single {
    width: 100%;
    height: 100%;
    position: relative;
}

.news-carousel-slide {
    cursor: pointer;
}

.news-carousel-slide img {
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease;
}

.news-carousel-slide:hover img {
    transform: scale(1.04);
}

.news-carousel-caption {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    padding: 22px 26px;
    background: linear-gradient(
        180deg,
        rgba(7, 20, 52, 0) 0%,
        rgba(7, 20, 52, 0.82) 100%
    );
    color: #ffffff;
    font-size: 20px;
    line-height: 1.5;
}

.news-carousel-pagination {
    bottom: 16px !important;
}

::v-deep .news-carousel-pagination .swiper-pagination-bullet {
    width: 10px;
    height: 10px;
    background: rgba(255, 255, 255, 0.56);
    opacity: 1;
}

::v-deep .news-carousel-pagination .swiper-pagination-bullet-active {
    background: #ffffff;
}

.gallery-section {
    padding: 50px 0 56px;
    background-image:
        url("~@/assets/image/home-bg-3.png");
}

.gallery-marquee {
    margin-top: 45px;
    margin-bottom: 42px;
    display: flex;
    gap: 18px;
    width: max-content;
    animation: gallery-scroll 48s linear infinite;
}

.gallery-section .section-shell {
    overflow: hidden;
}

.gallery-item {
    width: 320px;
    flex: 0 0 auto;
}

.gallery-item img {
    height: 225px;
    object-fit: cover;
}

.gallery-marquee:hover {
    animation-play-state: paused;
}

@keyframes gallery-scroll {
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(calc(-50% - 9px));
    }
}

.directory-section {
    padding: 62px 0 42px;
    background: #fff;
}

.section-note {
    margin-top: 12px;
    text-align: center;
    color: #666666;
    font-size: 20px;
}

.logo-grid {
    margin-top: 38px;
    display: grid;
    grid-template-columns: repeat(8, minmax(0, 1fr));
    gap: 24px 28px;
}

.logo-card {
    min-height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.logo-card img {
    max-width: 100%;
    max-height: 84px;
    object-fit: contain;
}

.directory-more {
    margin: 26px auto 0;
    width: fit-content;
    color: #8f8f8f;
    font-size: 18px;
    text-align: center;
    cursor: pointer;
}

.directory-more-icon {
    position: relative;
    width: 20px;
    height: 18px;
    margin: 10px auto 0;
}

.directory-more-icon::before,
.directory-more-icon::after {
    content: "";
    position: absolute;
    left: 50%;
    width: 12px;
    height: 12px;
    border-right: 1px solid #b0b0b0;
    border-bottom: 1px solid #b0b0b0;
    transform: translateX(-50%) rotate(45deg);
}

.directory-more-icon::before {
    top: -2px;
}

.directory-more-icon::after {
    top: 4px;
}


@media (max-width: 1750px) {
    .intro-copy {
        padding: 40px 46px;
        font-size: 23px;
        line-height: 42px;
    }
}

@media (max-width: 1680px) {
    .intro-copy {
        padding: 38px 44px;
        font-size: 22px;
        line-height: 42px;
    }
}

@media (max-width: 1536px) {
    .intro-copy {
        padding: 32px 36px;
        font-size: 20px;
        line-height: 36px;
    }
}

@media (max-width: 1440px) {
    .section-shell {
        width: 90%;
    }

    .section-title {
        font-size: 32px;
    }

    .intro-wrap {
        padding: 42px 0 48px;
    }

    .content-section {
        padding: 60px 0 0;
    }

    .news-panel {
        gap: 36px;
    }

    .intro-panel {
        grid-template-columns: minmax(0, 1.12fr) minmax(360px, 0.88fr);
        gap: 28px;
    }

    .intro-copy {
        min-height: 0;
        padding: 28px 30px;
        font-size: 18px;
        line-height: 32px;
    }

    .intro-video {
        min-height: 0;
    }

    .stat-card {
        min-height: 150px;
        padding: 24px 20px;
    }

    .stat-value {
        font-size: 62px;
    }

    .stat-label {
        font-size: 22px;
    }

    .stat-unit {
        font-size: 18px;
    }

    .news-date,
    .news-text {
        font-size: 21px;
        line-height: 1.55;
    }

    .news-cover {
        height: 340px;
    }

    .news-more-button {
        font-size: 17px;
    }

    .news-carousel-caption {
        padding: 18px 22px;
        font-size: 18px;
    }

    .gallery-marquee {
        gap: 16px;
    }

    .gallery-item img {
        height: 210px;
    }

    .section-note {
        font-size: 18px;
    }

    .logo-grid {
        gap: 20px 22px;
    }

}

@media (max-width: 1200px) {
    .section-shell {
        width: 92%;
    }

    .section-title {
        font-size: 30px;
    }

    .intro-wrap {
        padding: 38px 0 44px;
    }

    .content-section,
    .news-section {
        padding-top: 52px;
    }

    .news-panel {
        gap: 24px;
    }

    .intro-panel {
        grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
        gap: 24px;
    }

    .intro-copy {
        padding: 22px 24px;
        font-size: 16px;
        line-height: 28px;
    }

    .intro-video {
        min-height: 0;
    }

    .stats-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .stat-card {
        min-height: 140px;
    }

    .news-item {
        grid-template-columns: 150px minmax(0, 1fr);
    }

    .news-list.en .news-item {
        grid-template-columns: 150px minmax(0, 1fr);
    }

    .news-date,
    .news-text {
        font-size: 19px;
        line-height: 1.5;
    }

    .news-cover {
        height: 320px;
    }

    .logo-grid {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }

}

@media (max-width: 992px) {
    .section-title {
        font-size: 28px;
    }

    .content-section,
    .news-section {
        padding-top: 46px;
    }

    .intro-copy {
        padding: 20px 20px;
        font-size: 15px;
        line-height: 25px;
    }

    .stat-card {
        gap: 10px;
    }

    .stat-value {
        font-size: 54px;
    }

    .stat-label {
        font-size: 20px;
    }

    .stat-unit {
        font-size: 17px;
    }

    .news-item {
        grid-template-columns: 1fr;
        gap: 4px;
    }

    .news-list.en .news-item {
        grid-template-columns: 1fr;
    }

    .news-cover {
        height: 300px;
    }

    .logo-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

}

@media (max-width: 768px) {
    .section-shell {
        width: 94%;
    }

    .section-title {
        font-size: 24px;
    }

    .intro-wrap {
        padding: 32px 0 36px;
    }

    .content-section,
    .news-section,
    .gallery-section,
    .directory-section {
        padding-top: 36px;
    }

    .stats-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .intro-copy {
        padding: 18px 16px;
        font-size: 14px;
        line-height: 22px;
    }

    .stat-card {
        min-height: 0;
        padding: 22px 18px;
        gap: 12px;
    }

    .stat-value {
        font-size: 46px;
    }

    .news-item {
        grid-template-columns: 1fr;
        gap: 6px;
    }

    .news-list.en .news-item {
        grid-template-columns: 1fr;
    }

    .news-date,
    .news-text {
        font-size: 18px;
    }

    .news-cover {
        height: 260px;
    }

    .news-actions {
        margin-top: 24px;
    }

    .news-more-button {
        min-width: 152px;
        padding: 12px 24px;
        font-size: 16px;
    }

    .news-carousel-caption {
        padding: 14px 16px;
        font-size: 16px;
    }

    .gallery-item img {
        height: 190px;
    }

    .logo-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 18px 16px;
    }

    .talent-float {
        min-width: 220px;
        width: min(280px, 62vw);
    }

}

@media (max-width: 576px) {
    .section-title {
        font-size: 22px;
    }

    .intro-copy {
        padding: 16px 14px;
        font-size: 13px;
        line-height: 20px;
    }

    .news-date,
    .news-text {
        font-size: 16px;
    }

    .news-cover {
        height: 220px;
    }

    .news-more-button {
        width: 100%;
        max-width: 220px;
        padding: 12px 20px;
        font-size: 15px;
    }

    .news-carousel-caption {
        padding: 12px 14px;
        font-size: 14px;
    }

    .gallery-item img {
        height: 170px;
    }

    .section-note,
    .directory-more {
        font-size: 16px;
    }

    .logo-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

}
</style>

<style lang="less">
#app.home-responsive-layout {
    min-width: 0;
    width: 100%;
}
</style>


